import axios from 'axios';
import type { ToastMessageOptions } from 'primevue/toast';
import { newErrorId } from './errorId';
import { TOAST_LIFE } from './notify';

export interface ApiErrorDescription {
  /** Correlation id: the backend requestId when present, otherwise a fresh one. */
  id: string;
  status?: number;
  method?: string;
  url?: string;
  /** Same failure → same key, so a burst of identical errors toasts once. */
  dedupeKey: string;
  /** Only idempotent reads are deduped; a retried save must always get an answer. */
  dedupable: boolean;
  toast: ToastMessageOptions;
}

const summaryFor = (status?: number): string => {
  if (status === 403) return 'Adgang nægtet';
  if (status === 404) return 'Ikke fundet';
  if (status && status >= 500) return 'Serverfejl';
  if (status && status >= 400) return 'Forespørgsel afvist';
  return 'Netværksfejl';
};

const messageFrom = (error: any): string => {
  const backendMsg = error?.response?.data?.message;
  if (Array.isArray(backendMsg)) return backendMsg.join('. ');
  if (typeof backendMsg === 'string' && backendMsg) return backendMsg;
  return error?.message || 'Ukendt fejl';
};

/**
 * Pure description of a failed `api` call for the response interceptor.
 * Returns null when nothing must be shown: `silent: true` requests, the auth
 * endpoints (handled by the refresh/login flow) and cancelled requests.
 */
export const describeApiError = (error: any): ApiErrorDescription | null => {
  const cfg = error?.config ?? {};
  if (cfg.silent === true) return null;
  if (/\/auth\/(login|refresh|logout)/.test(cfg.url ?? '')) return null;
  if (axios.isCancel?.(error)) return null;

  const status: number | undefined = error?.response?.status;
  const id: string = error?.response?.data?.requestId ?? newErrorId();
  const summary = summaryFor(status);
  const baseMsg = messageFrom(error);

  return {
    id,
    status,
    method: cfg.method?.toUpperCase(),
    url: cfg.url,
    dedupeKey: `${status ?? 'network'}|${summary}|${baseMsg}`,
    // Unknown method → not deduped: fail toward feedback rather than silence.
    dedupable: (cfg.method ?? '').toLowerCase() === 'get',
    toast: {
      severity: !status || status >= 500 ? 'error' : 'warn',
      summary,
      detail: `${baseMsg} (kode: ${id})`,
      life: TOAST_LIFE.apiError,
    },
  };
};

export const API_TOAST_DEDUPE_MS = 4000;
const recentlyToasted = new Map<string, number>();

/**
 * True when no identical toast was shown within the dedupe window, and records
 * this one. Eleven parallel store loads failing on a backend restart therefore
 * produce one "Netværksfejl" toast instead of eleven; every error is still logged.
 */
export const shouldShowApiToast = (key: string, now: number = Date.now()): boolean => {
  const last = recentlyToasted.get(key);
  for (const [k, t] of recentlyToasted) {
    if (now - t >= API_TOAST_DEDUPE_MS) recentlyToasted.delete(k);
  }
  if (last !== undefined && now - last < API_TOAST_DEDUPE_MS) return false;
  recentlyToasted.set(key, now);
  return true;
};

/** Test hook. */
export const resetApiToastDedupe = (): void => recentlyToasted.clear();

export interface ReportApiErrorDeps {
  notify: (toast: ToastMessageOptions) => void;
  log?: (...args: unknown[]) => void;
  now?: () => number;
}

/**
 * The interceptor body, side effects injected so it can be tested: describe the
 * failure, log it with its correlation code, and toast it — deduped only for GET
 * bursts (a retried POST/PATCH/DELETE always answers). Returns the description,
 * or null when the request was silent/auth/cancelled.
 */
export const reportApiError = (error: any, deps: ReportApiErrorDeps): ApiErrorDescription | null => {
  const described = describeApiError(error);
  if (!described) return null;
  (deps.log ?? console.error)(`[api error ${described.id}]`, described.status, described.method, described.url, error);
  const show = described.dedupable ? shouldShowApiToast(described.dedupeKey, deps.now?.()) : true;
  if (show) deps.notify(described.toast);
  return described;
};
